import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    OutlinedInput,
    InputAdornment,
    IconButton,
    Chip,
    Stack
} from '@mui/material';
import AssessmentCard from '../../components/Assessment/AssessmentCard';
import SearchIcon from '@mui/icons-material/Search';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

interface SelectingAssessmentsProps {
    assessmentsList: Array<{
        id: number;
        title: string;
        description: string;
    }>;
    selectedAssessments: Array<{
        id: number;
        title: string;
        description: string;
    }>;
    searchQuery: string;
    handleSearch: () => void;
    handleSelectAssessment: (assessment: any) => void;
    handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleDrop: (event: React.DragEvent<HTMLDivElement>) => void;
    handleDragStart: (event: React.DragEvent<HTMLDivElement>, id: number) => void;
    handleDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
    handleSubmit: (event: React.FormEvent) => Promise<void>;
}

const SelectingAssessments: React.FC<SelectingAssessmentsProps> = ({
    assessmentsList,
    selectedAssessments,
    searchQuery,
    handleSearch,
    handleSelectAssessment,
    handleSearchChange,
    handleDrop,
    handleDragStart,
    handleDragOver,
    handleSubmit,
}) => {
    // local input state to debounce calls to parent handler
    const [inputQuery, setInputQuery] = useState<string>(searchQuery || '');

    useEffect(() => {
        const timer = setTimeout(() => {
            // propagate the debounced value to parent
            handleSearchChange({
                target: { value: inputQuery },
            } as unknown as React.ChangeEvent<HTMLInputElement>);
        }, 600);

        return () => clearTimeout(timer);
        // only restart debounce when inputQuery changes
    }, [inputQuery, handleSearchChange]);
    return (
        <Box>
            <Grid container spacing={3}>
                <Grid item xs={12} md={8} sx={{ borderRight: 1, borderColor: 'divider', paddingRight: 2, mt: 4 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>All Assessments</Typography>
                    <OutlinedInput
                        fullWidth
                        placeholder="Search Assessments"
                        value={inputQuery}
                        onChange={(e) => setInputQuery(e.target.value)}
                        sx={{ mb: 3 }}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton onClick={handleSearch}>
                                    <SearchIcon />
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                    <Grid container spacing={1}>
                        {assessmentsList.map((assessment, index) => (
                            <Grid item xs={12} sm={6} key={assessment.id}>
                                <AssessmentCard
                                    assessmentTitle={assessment.title}
                                    assessmentId={assessment.id}
                                    assessmentDescription={assessment.description}
                                    onSelect={() => handleSelectAssessment(assessment)}
                                    isSelected={selectedAssessments.some(selected => selected.id === assessment.id)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
                <Grid item xs={12} md={4} sx={{ paddingLeft: 2, mt: 4 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>Selected Assessments</Typography>
                    <Box onDrop={handleDrop} onDragOver={handleDragOver}>
                        {selectedAssessments.map((assessment, index) => (
                            <Box
                                key={assessment.id}
                                draggable
                                onDragStart={(event) => handleDragStart(event, assessment.id)}
                                data-id={assessment.id}
                                sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}
                            >
                                <IconButton>
                                    <DragIndicatorIcon />
                                </IconButton>
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                    <Chip label={assessment.title} variant="outlined" clickable />
                                </Stack>
                            </Box>
                        ))}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SelectingAssessments;
